import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import { PageBody } from "@/components/ui/layout"
import { EducatorsCustomClassesAcceptedStudentSet } from "@/types/serializers"

import { CalendarClock, Inbox } from "lucide-react"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card"
import {
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  Empty,
} from "@/components/ui/empty"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import {
  calculateEventStartCalendarTimezoneCoerced,
  displayPlainTime,
} from "@/lib/timetableZonedDatetime"
import { Link, useProps } from "@/lib/page-rendering"

type IndexProps = {
  live_class_instances: Array<EducatorsCustomClassesAcceptedStudentSet>
  class_name: string
}

const useIndexProps = () => useProps<IndexProps>()

export default function Page() {
  const { live_class_instances, class_name } = useIndexProps()
  const { t } = useTranslation("classes", { keyPrefix: "students" })

  return (
    <>
      <EducatorPageChrome records={{ "educators/classes/edit": class_name }} />
      <PageBody role="region">
        <ul className="flex flex-col gap-6">
          {live_class_instances.length === 0 ? (
            <Card>
              <CardContent>
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia
                      variant="icon"
                      className="text-muted size-20 bg-white"
                    >
                      <Inbox className="size-16" />
                    </EmptyMedia>
                    <EmptyTitle>{t("empty_list_title")}</EmptyTitle>
                    <EmptyDescription>
                      {t("empty_list_description")}
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              </CardContent>
            </Card>
          ) : (
            live_class_instances.map((prioritySet) => (
              <PriorityCard
                prioritySet={prioritySet}
                key={prioritySet.priority_uuid}
              />
            ))
          )}
        </ul>
      </PageBody>
    </>
  )
}

function PriorityCard({
  prioritySet,
}: {
  prioritySet: EducatorsCustomClassesAcceptedStudentSet
}) {
  const { t } = useTranslation("classes", { keyPrefix: "students" })
  return (
    <Card data-testid="priority-students-card">
      <CardHeader>
        <Accordion type="single" collapsible>
          <AccordionItem value="header">
            <AccordionTrigger>
              <CardTitle>
                {t("priority_title", {
                  rank: prioritySet.priority_rank,
                  instance: prioritySet.instance_index + 1,
                })}
              </CardTitle>
              <Badge className="bg-brand-light-grey ml-auto">
                <CalendarClock />
                {t("sessions", { count: prioritySet.sessions.length })}
              </Badge>
            </AccordionTrigger>
            <AccordionContent className="flex flex-row flex-wrap gap-4">
              {prioritySet.sessions.map((session, index) => (
                <TimetableSessionBadge
                  session={session}
                  eventTimezoneId={prioritySet.event_timezone_id}
                  key={index}
                />
              ))}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <ul className="grid w-full grid-cols-2 gap-4">
          {prioritySet.students.map((student) => (
            <Card key={student.student_id} data-testid="student-card">
              <CardHeader>
                <CardTitle>{student.student_name}</CardTitle>
                <CardDescription>{student.school_name}</CardDescription>
              </CardHeader>
              <CardFooter>
                <Button size="sm" className="w-full" asChild>
                  <Link
                    href={`./students/${student.student_id}`}
                    data={{
                      priority_uuid: prioritySet.priority_uuid,
                      instance_index: prioritySet.instance_index,
                    }}
                    className="w-full"
                  >
                    {t("view_student")}
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function TimetableSessionBadge({
  session,
  eventTimezoneId,
}: {
  session: EducatorsCustomClassesAcceptedStudentSet["sessions"][number]
  eventTimezoneId: string
}) {
  const start = calculateEventStartCalendarTimezoneCoerced({
    startTime: session.start_time!,
    day: session.day!,
    startTemporalZonedDateTime: Temporal.Instant.from(
      "2025-01-06T00:00:00Z",
    ).toZonedDateTimeISO("UTC"),
    calendarTimezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
    eventTimezoneId: eventTimezoneId,
  })
  const dayString = Intl.DateTimeFormat(undefined, {
    weekday: "long",
    timeZone: start.timeZoneId,
  }).format(start.toInstant())
  const startStr = displayPlainTime(start.toPlainTime())
  const endStr = displayPlainTime(
    start.add({ minutes: session.duration }).toPlainTime(),
  )

  return (
    <Badge className="bg-brand-light-grey">{`${dayString}: ${startStr} - ${endStr}`}</Badge>
  )
}
